/**
 * Edit a candidate's fields, and set or clear consent — `PATCH
 * /recruiting/candidates/{id}`, WP-W2.
 *
 * A partial update: the body carries **only the fields the operator changed**, and
 * `RecruitingCandidateUpdateRequest` requires at least one. The submit is disabled until
 * something differs from the loaded candidate — a true statement ("nothing to save"),
 * which is also the shape that keeps an empty `PATCH`, refused `400`, from being sent.
 *
 * The nullable fields (`email`, `phone`, `source`, `current_title`, `current_company`,
 * `location`, `linkedin_url`) send `null` to **clear**, a different request from
 * omitting them; `full_name` is non-nullable, so a blanked name is simply not part of
 * the change set.
 *
 * **Consent** is `privacy_consent_at`, an editable timestamp. It is not a text field —
 * a person cannot sensibly type an ISO instant — so it is two honest verbs: *record
 * now* (sends the current instant) and *clear* (sends `null`). Each fires its own
 * one-field `PATCH` through `onConsent`, kept apart from the field change set so a name
 * edit and a consent change never ride in one ambiguous body.
 *
 * There is no `If-Match` on this endpoint, so the screen re-reads after any save.
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { formatInstant } from '../../i18n/datetime';
import type {
  CandidateSource,
  RecruitingCandidate,
  RecruitingCandidateUpdateRequest,
} from '../../api/recruiting';
import { CANDIDATE_SOURCES } from '../../api/recruiting';
import { SOURCE_LABEL } from '../../recruiting/candidate';
import type { WorkforceApiError } from '../../api/errors';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { StatusNote } from '../../states/StatusNote';

const NAME_MAX = 500;

export interface CandidateEditFormProps {
  readonly candidate: RecruitingCandidate;
  readonly pending: boolean;
  readonly error: WorkforceApiError | null;
  readonly saved: boolean;
  readonly locale: string;
  readonly timezone: string;
  readonly onSubmit: (body: RecruitingCandidateUpdateRequest) => void;
  readonly onConsent: (value: string | null) => void;
}

export function CandidateEditForm({
  candidate,
  pending,
  error,
  saved,
  locale,
  timezone,
  onSubmit,
  onConsent,
}: CandidateEditFormProps): ReactNode {
  const { t } = useI18n();
  const [fullName, setFullName] = useState(candidate.full_name);
  const [email, setEmail] = useState(candidate.email ?? '');
  const [phone, setPhone] = useState(candidate.phone ?? '');
  const [source, setSource] = useState<'' | CandidateSource>(candidate.source ?? '');
  const [currentTitle, setCurrentTitle] = useState(candidate.current_title ?? '');
  const [currentCompany, setCurrentCompany] = useState(candidate.current_company ?? '');
  const [location, setLocation] = useState(candidate.location ?? '');
  const [linkedin, setLinkedin] = useState(candidate.linkedin_url ?? '');

  // The change set, computed once so the submit's enabled state and the body it sends
  // cannot disagree. A nullable field blanked becomes `null` (clear); `full_name`
  // blanked is not a change (non-nullable, empty is invalid). Built mutably, handed
  // over as the readonly request shape.
  const draft: {
    full_name?: string;
    email?: string | null;
    phone?: string | null;
    source?: CandidateSource | null;
    current_title?: string | null;
    current_company?: string | null;
    location?: string | null;
    linkedin_url?: string | null;
  } = {};

  const trimmedName = fullName.trim();
  if (trimmedName !== '' && trimmedName !== candidate.full_name) draft.full_name = trimmedName;
  diffNullable(email, candidate.email, (value) => {
    draft.email = value;
  });
  diffNullable(phone, candidate.phone, (value) => {
    draft.phone = value;
  });
  const currentSource = candidate.source ?? '';
  if (source !== currentSource) draft.source = source === '' ? null : source;
  diffNullable(currentTitle, candidate.current_title, (value) => {
    draft.current_title = value;
  });
  diffNullable(currentCompany, candidate.current_company, (value) => {
    draft.current_company = value;
  });
  diffNullable(location, candidate.location, (value) => {
    draft.location = value;
  });
  diffNullable(linkedin, candidate.linkedin_url, (value) => {
    draft.linkedin_url = value;
  });

  const change: RecruitingCandidateUpdateRequest = draft;
  const hasChange = Object.keys(draft).length > 0;

  return (
    <section className="wp-section" aria-labelledby="w2-edit-heading" data-testid="w2-edit">
      <h3 className="wp-section__title" id="w2-edit-heading">
        {t('recruiting.candidates.edit.title')}
      </h3>

      <form
        className="wp-filters"
        onSubmit={(event) => {
          event.preventDefault();
          if (!hasChange || pending) return;
          onSubmit(change);
        }}
      >
        <TextField
          id="w2-edit-name"
          label={t('recruiting.candidates.edit.fullName')}
          value={fullName}
          maxLength={NAME_MAX}
          onChange={setFullName}
        />
        <TextField
          id="w2-edit-email"
          label={t('recruiting.candidates.edit.email')}
          value={email}
          type="email"
          onChange={setEmail}
        />
        <TextField
          id="w2-edit-phone"
          label={t('recruiting.candidates.edit.phone')}
          value={phone}
          onChange={setPhone}
        />

        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w2-edit-source">
            {t('recruiting.candidates.edit.source')}
          </label>
          <select
            className="wp-field__input"
            id="w2-edit-source"
            data-testid="w2-edit-source"
            value={source}
            onChange={(event) => {
              setSource(event.target.value as '' | CandidateSource);
            }}
          >
            <option value="">{t('recruiting.candidates.source.none')}</option>
            {CANDIDATE_SOURCES.map((value) => (
              <option key={value} value={value}>
                {t(SOURCE_LABEL[value])}
              </option>
            ))}
          </select>
        </div>

        <TextField
          id="w2-edit-title"
          label={t('recruiting.candidates.edit.currentTitle')}
          value={currentTitle}
          onChange={setCurrentTitle}
        />
        <TextField
          id="w2-edit-company"
          label={t('recruiting.candidates.edit.currentCompany')}
          value={currentCompany}
          onChange={setCurrentCompany}
        />
        <TextField
          id="w2-edit-location"
          label={t('recruiting.candidates.edit.location')}
          value={location}
          onChange={setLocation}
        />
        <TextField
          id="w2-edit-linkedin"
          label={t('recruiting.candidates.edit.linkedin')}
          value={linkedin}
          type="url"
          onChange={setLinkedin}
        />

        <div className="wp-filters__actions">
          <button
            className="wp-button wp-button--primary"
            type="submit"
            data-testid="w2-edit-submit"
            disabled={!hasChange || pending}
          >
            {pending
              ? t('recruiting.candidates.edit.saving')
              : t('recruiting.candidates.edit.save')}
          </button>
          {hasChange ? null : (
            <p className="wp-small wp-muted" data-testid="w2-edit-nochange">
              {t('recruiting.candidates.edit.noChange')}
            </p>
          )}
        </div>
      </form>

      <div className="wp-section__subsection" data-testid="w2-consent">
        <h4 className="wp-section__title">{t('recruiting.candidates.consent.title')}</h4>
        <p className="wp-small wp-muted" data-testid="w2-consent-status">
          {candidate.privacy_consent_at === null
            ? t('recruiting.candidates.detail.noConsent')
            : t('recruiting.candidates.detail.consent', {
                when: formatInstant(candidate.privacy_consent_at, locale, timezone),
              })}
        </p>
        <div className="wp-filters__actions">
          <button
            className="wp-button"
            type="button"
            data-testid="w2-consent-record"
            disabled={pending}
            onClick={() => {
              onConsent(new Date().toISOString());
            }}
          >
            {t('recruiting.candidates.consent.record')}
          </button>
          {candidate.privacy_consent_at === null ? null : (
            <button
              className="wp-button"
              type="button"
              data-testid="w2-consent-clear"
              disabled={pending}
              onClick={() => {
                onConsent(null);
              }}
            >
              {t('recruiting.candidates.consent.clear')}
            </button>
          )}
        </div>
      </div>

      {error === null ? null : (
        <ApiErrorNote
          error={error}
          subject={t('recruiting.candidates.subjectWord')}
          testId="w2-edit-error"
        />
      )}
      {saved ? (
        <StatusNote severity="low" title={t('recruiting.candidates.edit.ok')} testId="w2-edit-ok" />
      ) : null}
    </section>
  );
}

/** A field is a change when its trimmed value differs from the loaded one; blank clears. */
function diffNullable(
  value: string,
  current: string | null,
  set: (next: string | null) => void,
): void {
  const trimmed = value.trim();
  const currentValue = current ?? '';
  if (trimmed === currentValue) return;
  set(trimmed === '' ? null : trimmed);
}

interface TextFieldProps {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly type?: 'text' | 'email' | 'url';
  readonly maxLength?: number;
  readonly onChange: (value: string) => void;
}

function TextField({
  id,
  label,
  value,
  type = 'text',
  maxLength,
  onChange,
}: TextFieldProps): ReactNode {
  return (
    <div className="wp-filters__group">
      <label className="wp-field__label" htmlFor={id}>
        {label}
      </label>
      <input
        className="wp-field__input"
        id={id}
        data-testid={id}
        type={type}
        value={value}
        autoComplete="off"
        {...(maxLength === undefined ? {} : { maxLength })}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
    </div>
  );
}
